/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment, useEffect } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import {
  Box,
  Divider,
  FormHelperText,
  Typography,
  Grid,
  useTheme,
} from '@material-ui/core';

import { GridItem } from '../../../../pge-plus-common/Grid';
import colors from '../../../../../themes/main-colors';
import { DropdownMenu } from '../../../../pge-plus-common/DropdownMenu';
import CurrencyTextField from '../../../../currency-text-field/CurrencyTextField';
import { Question } from '../../../../pge-plus-common/Typography';
import RadioButtonGroup from '../../../../../components/pge-plus-common/RadioButtonGroup';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import ROUTES from '../../../../../routes';
import { CheckBoxWithPaper } from '../../../../pge-plus-common/CheckBoxWithPaper';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import useFormState from '../../../../../hooks/useFormState';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { higherRabatesValidateFunction } from '../rebateOnlyForm.rules';
import { RebateConfirmIQBD } from '../rebateOnlyForm.types';
import {
  getSmartChargingRebate,
  getRebateTypeCategory,
  isIQBDQuestionApplicable,
} from '../rebateOnlyForm.utils';
import {
  SectionErRebateConfirm,
  ApplicationErSectionType,
  RebateSmartCharging,
  EvRebateEnrollments,
  SectionErRebateConfirmData,
} from '../../../../../__generated__/pgeplus-types';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import { Link } from 'gatsby';
import { sectionFragmentER } from '../../../../pge-plus-common/queries/fragments';
import gql from 'not-graphql-tag';
import { useGetApplicationSectionERQuery } from '../../../../../hooks/pge-plus/useGetApplicationSectionER';
import Backdrop from '../../../../backdrop/Backdrop';

export const getApplicationSectionQuery = gql`
  query($input: GetApplicationSectionInput!) {
    getApplicationSection(input: $input) {
      ...sectionFragment
    }
  }
  ${sectionFragmentER}
`;

const CheckHigherRebatesEligibility: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_REBATE_CONFIRM,
  );

  const {
    formState: { rebateConfirm, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { setErrorNotification } = useUtil();
  const theme = useTheme();
  const { wrapWithLoader } = useWrapWithLoader();

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const { sectionData, loading } = useGetApplicationSectionERQuery(
    application?.id!,
    sectionDataRebateConfirm?.id,
  );

  const form = useFormState(rebateConfirm.iqbd || {}, {
    validate: higherRabatesValidateFunction(),
    validationContext: {
      content,
    },
  });

  const householdSizeOptions = [
    ...Array.from({ length: 11 }, (_, i) => ({
      label: `${i + 1}`,
      value: `${i + 1}`,
    })),
    { label: '12+', value: '12' },
  ];

  useEffect(() => {
    if (sectionData && !loading) {
      const sectionDataRebateConfirmData = sectionData?.getApplicationSection
        ?.data as SectionErRebateConfirmData;

      const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
        sectionDataRebateConfirmData,
      );
      updateFormState('rebateConfirm', {
        ...updatedValues,
        //Making this undefined to allow moving forward avoiding UNABLE_TO_CONNECT_APP
        //ineligiblity which was set in charger instructions screen
        chargerActivatedInAppConfirmed: undefined,
      });

      if (
        !isIQBDQuestionApplicable(
          sectionDataRebateConfirmData?.rebateInfo?.existingEnrollments,
        )
      ) {
        handleRebateEligiblityCheckAndRedirect(
          getSmartChargingRebate(
            sectionDataRebateConfirmData?.preRebateEligibility2,
            sectionDataRebateConfirmData?.preRebateEligibility1,
          ),
          sectionDataRebateConfirmData?.rebateInfo?.existingEnrollments,
          false,
          sectionDataRebateConfirmData?.rebateConfirmInput?.iqbdQualification
            ?.checkEligibility,
          sectionDataRebateConfirmData?.rebateConfirmInput?.chargerInstallInfo
            ?.isChargerPurchased === false,
        );
      }
    }
  }, [sectionData, loading]);

  const handleSubmit = wrapWithLoader(async (data: RebateConfirmIQBD) => {
    try {
      if (!data.checkEligibility) {
        // Removing previously saved value when the user clicked Yes for checkEligibility
        delete data.grossIncome;
        delete data.grossIncomeConfirmed;
        delete data.houseHoldSize;
      }

      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          { ...rebateConfirm, iqbd: data },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
      );

      if (!response.data?.updateSectionERRebateConfirm.section.errors?.length) {
        const updatedSectionData =
          response.data?.updateSectionERRebateConfirm?.section.data;

        //Store the data in global store
        if (updatedSectionData) {
          const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
            updatedSectionData,
          );
          updateFormState('rebateConfirm', {
            ...updatedValues,
          });
        }

        void handleRebateEligiblityCheckAndRedirect(
          getSmartChargingRebate(
            updatedSectionData?.preRebateEligibility2,
            updatedSectionData?.preRebateEligibility1,
          ),
          updatedSectionData?.rebateInfo.existingEnrollments!,
          true,
          data.checkEligibility,
          updatedSectionData?.rebateConfirmInput?.chargerInstallInfo
            ?.isChargerPurchased === false,
        );
      } else {
        throw new Error();
      }
    } catch (error) {
      console.log('error', error);
      setErrorNotification(true);
    }
  });

  const handleRebateEligiblityCheckAndRedirect = (
    evRebate: RebateSmartCharging | undefined,
    existingEnrollments: EvRebateEnrollments,
    isAbleToRequestIQBD = true,
    checkEligibility?: boolean | null,
    isChargerAlreadyPresent = false,
  ) => {
    try {
      if (isChargerAlreadyPresent) {
        void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_CHARGER_INFORMATION);
        return;
      }

      const rebateSmartCharging = evRebate;

      // Note: Falls back to .amount since RebateEVPulse-typed rebates have no .charger field
      const state: any = {
        chargerAmount: rebateSmartCharging?.charger ?? rebateSmartCharging?.amount,
        // CUP-24444: unchanged for charger-path, still correct for vehicle-path since RebateSmartCharging is the same shape either way
        panelUpgradeAmount: rebateSmartCharging?.panelUpgrade,
        isAbleToRequestIQBD,
      };

      //Navigation logic
      // CUP-24444: Extracted the inline iqbd/higher/standard if-else chain into the shared getRebateTypeCategory() helper, same behavior
      state['rebateType'] = getRebateTypeCategory(
        existingEnrollments?.iqbd,
        rebateSmartCharging?.isHigherDiscount,
        checkEligibility,
      );
      if (state.rebateType) {
        void navigate(
          state.rebateType === 'standard'
            ? ROUTES.PGE_PLUS_REBATE_ONLY_STANDARD_REBATES_ELIGIBILE
            : ROUTES.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_ELIGIBILE,
          {
            state,
          },
        );
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error();
    }
  };

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            iqbd: form.values,
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(ROUTES.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <form onSubmit={form.submit(values => handleSubmit(values), console.log)}>
      <Box>
        {loading && <Backdrop forceOpen />}
        <Box
          css={{
            '& a': { textDecoration: 'none', fontWeight: 700 },
            marginBottom: theme.spacing(1),
          }}
        >
          <Question
            title={content.get('WOULD_YOU_LIKE_TO_CHECK')}
            detail={richText(content.get('REBATE_ELIGIBILITY_DETAILS'))}
          />
        </Box>
        <Box mt={3} display={'flex'} justifyContent={'center'}>
          <Grid xs={12} md={6}>
            <RadioButtonGroup
              options={[
                {
                  label: content.get('REUSABLE_YES') || '',
                  value: content.get('REUSABLE_YES') || '',
                },
                {
                  label: content.get('REUSABLE_NO') || '',
                  value: content.get('REUSABLE_NO') || '',
                },
              ]}
              state={
                form.values.checkEligibility === true
                  ? content.get('REUSABLE_YES') || ''
                  : form.values.checkEligibility === false
                  ? content.get('REUSABLE_NO') || ''
                  : ''
              }
              setState={val => {
                void form.setValue(
                  'checkEligibility',
                  val === content.get('REUSABLE_YES'),
                );
              }}
            />
          </Grid>
        </Box>
        {form.errors.checkEligibility && (
          <FormHelperText error>{form.errors.checkEligibility}</FormHelperText>
        )}
        {form.values.checkEligibility && (
          <React.Fragment>
            <Grid
              container
              spacing={2}
              css={{ justifyContent: 'space-between', margin: '16px 0' }}
            >
              <Grid item xs={12} md={6}>
                <DropdownMenu
                  name={content.get('HOUSEHOLD_SIZE') || ''}
                  options={householdSizeOptions}
                  state={form.values.houseHoldSize?.toString() || ''}
                  setState={val => {
                    void form.setValue('houseHoldSize', Number(val));
                  }}
                  errorMsg={form.errors.houseHoldSize?.toString() || ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CurrencyTextField
                  css={{
                    margin: theme.spacing(0, 0, 0, 0),
                    width: '100%',
                    '& label': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '90%',
                      '&.MuiInputLabel-shrink': {
                        overflow: 'unset',
                      },
                    },
                  }}
                  label={content.get('ANNUAL_HOUSEHOLD_INCOME') || ''}
                  name={content.get('ANNUAL_HOUSEHOLD_INCOME') || ''}
                  onChange={(e: any) => {
                    void form.setValue(
                      'grossIncome',
                      Math.round(e.target.value),
                    );
                  }}
                  value={form.values.grossIncome || ''}
                  error={false}
                />
                {form.errors.grossIncome && (
                  <FormHelperText error>
                    {form.errors.grossIncome}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
            <CheckBoxWithPaper
              option={{
                label: content.get('I_CONFIRM_THIS_IS_MY_INCOME') || '',
                value: content.get('I_CONFIRM_THIS_IS_MY_INCOME') || '',
              }}
              state={
                form.values.grossIncomeConfirmed
                  ? content.get('I_CONFIRM_THIS_IS_MY_INCOME') || ''
                  : ''
              }
              setState={val =>
                form.setValue(
                  'grossIncomeConfirmed',
                  val === content.get('I_CONFIRM_THIS_IS_MY_INCOME')
                    ? true
                    : undefined,
                )
              }
              errorMsg={form.errors.grossIncomeConfirmed?.toString()}
            />
            <Box marginTop={2} marginBottom={6}>
              <PopoverToolTip
                baseContent={content.get(
                  'LEARN_MORE_ABOUT_GROSS_ANNUAL_INCOME',
                )}
                popoverContent={content.get('GROSS_ANNUAL_INCOME_TOOLTIP')}
              />
            </Box>
          </React.Fragment>
        )}
        <Divider css={{ color: colors.gray93, margin: '20px 0 20px 0' }} />
        <ProceedOrCancelButton
          back={ROUTES.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION}
          backLabel={content.get('REUSABLE_BUTTON_BACK')}
          disable={
            form.values.checkEligibility === undefined ||
            form.values.checkEligibility === null ||
            (form.values.checkEligibility === true &&
              (!form.values.houseHoldSize ||
                !form.values.grossIncome ||
                !form.values.grossIncomeConfirmed))
          }
        />
      </Box>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </form>
  );
};

export default CheckHigherRebatesEligibility;
